import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPaperPlane, faTimes, faCircle } from '@fortawesome/free-solid-svg-icons';
import { io } from 'socket.io-client';
import './ChatDialog.css';

const SOCKET_URL = 'https://catalina.xseu.net';
const SOCKET_PATH = '/3393/socket.io';

const ChatDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [userName, setUserName] = useState('');
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const USER_ID_KEY = 'megahit_chat_user_id';
  const USER_NAME_KEY = 'megahit_chat_user_name';

  const getUserId = () => {
    let userId = localStorage.getItem(USER_ID_KEY);
    if (!userId) {
      userId = 'web_' + crypto.randomUUID();
      localStorage.setItem(USER_ID_KEY, userId);
    }
    return userId;
  };

  useEffect(() => {
    const storedName = localStorage.getItem(USER_NAME_KEY);
    if (storedName) {
      setUserName(storedName);
      setShowNameInput(false);
      connectToChat(storedName);
    } else {
      setShowNameInput(true);
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const connectToChat = (name) => {
    if (socketRef.current?.connected) return;

    const socket = io(SOCKET_URL, {
      path: SOCKET_PATH,
      rejectUnauthorized: false,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      socket.emit('join_chat', { userId: getUserId(), userName: name });
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.on('message_history', (history) => {
      setMessages(history);
    });

    socket.on('new_message', (message) => {
      setMessages(prev => {
        if (prev.find(m => m.id === message.id)) {
          return prev;
        }
        const newMessages = [...prev, message];
        
        if (!isOpen && message.isFromStudio) {
          setUnreadCount(prev => prev + 1);
          setIsBlinking(true);
          setTimeout(() => setIsBlinking(false), 3000);
        }
        
        return newMessages;
      });
    });
  };

  const handleSetUserName = async () => {
    if (userName.trim().length < 2) {
      alert('Please enter a name with at least 2 characters');
      return;
    }
    localStorage.setItem(USER_NAME_KEY, userName.trim());
    setShowNameInput(false);
    connectToChat(userName.trim());
  };

  const handleSendMessage = () => {
    if (!inputText.trim() || !socketRef.current?.connected) return;

    socketRef.current.emit('send_message', { text: inputText.trim() });
    setInputText('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (msg) => msg.senderId === getUserId();
  const isStudio = (msg) => msg.isFromStudio;

  const handleOpenChat = () => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  if (!isOpen) {
    return (
      <div className={`chat-toggle ${isBlinking ? 'blinking' : ''}`} onClick={handleOpenChat}>
        <FontAwesomeIcon icon={faComments} size="lg" />
        <span className="chat-toggle-text">Chat</span>
        {unreadCount > 0 && (
          <span className="chat-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </div>
    );
  }

  return (
    <div className="chat-dialog">
      <div className="chat-header">
        <div className="chat-header-title">
          <FontAwesomeIcon icon={faComments} style={{ marginRight: 8 }} />
          Studio Chat
        </div>
        <div className="chat-header-right">
          <span className={`connection-status ${isConnected ? 'connected' : ''}`}>
            <FontAwesomeIcon icon={faCircle} size="xs" />
            {isConnected ? 'Online' : ''}
          </span>
          <button className="chat-close-btn" onClick={() => setIsOpen(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      {showNameInput ? (
        <div className="chat-name-input">
          <h3>Welcome to MegaHit Chat!</h3>
          <p>Enter your name to start chatting with the studio</p>
          <input
            type="text"
            placeholder="Your name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            maxLength={30}
            onKeyPress={(e) => e.key === 'Enter' && handleSetUserName()}
          />
          <button onClick={handleSetUserName} className="chat-start-btn">
            Start Chatting
          </button>
        </div>
      ) : (
        <>
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-empty">
                <FontAwesomeIcon icon={faComments} size="2x" />
                <p>No messages yet.<br />Be the first to say hello!</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`chat-message ${isStudio(msg) ? 'studio' : ''} ${isOwnMessage(msg) ? 'own' : ''}`}
                >
                  <div className="message-sender">
                    {isStudio(msg) ? '🎙️ Studio' : msg.senderName}
                  </div>
                  <div className="message-text">{msg.text}</div>
                  <div className="message-time">{formatTime(msg.timestamp)}</div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <textarea
              placeholder="Type a message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!isConnected}
            />
            <button
              className={`chat-send-btn ${inputText.trim() && isConnected ? 'active' : ''}`}
              onClick={handleSendMessage}
              disabled={!inputText.trim() || !isConnected}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatDialog;
